import React, { useLayoutEffect } from 'react';
import UpgradePrompt from './UpgradePrompt.jsx';
import usePlanEntitlements from '../../hooks/usePlanEntitlements.js';
import { useHeader } from '../../context/HeaderContext.jsx';

const workspaceUpgradeUrl = {
  personal: '/billing',
  organization: '/org/billing'
};

const previewConfig = {
  timeline_view: {
    title: 'Timeline planning',
    columns: ['Discovery', 'Build', 'Review', 'Launch'],
    rows: [
      { label: 'Project roadmap', width: '72%', offset: '6%' },
      { label: 'Dependency chain', width: '48%', offset: '28%' },
      { label: 'Release window', width: '36%', offset: '56%' }
    ]
  },
  sticky_notes: {
    title: 'Workspace notes',
    columns: ['Ideas', 'Follow-ups', 'Shared'],
    rows: [
      { label: 'Sprint notes', width: '58%', offset: '4%' },
      { label: 'Decisions', width: '70%', offset: '18%' },
      { label: 'Personal reminders', width: '42%', offset: '44%' }
    ]
  },
  audit_logs: {
    title: 'Activity history',
    columns: ['Actor', 'Action', 'Time'],
    rows: [
      { label: 'Member updated task', width: '80%', offset: '3%' },
      { label: 'Project setting changed', width: '64%', offset: '16%' },
      { label: 'Export requested', width: '44%', offset: '34%' }
    ]
  },
  module_finance: {
    title: 'Finance workspace',
    columns: ['Budget', 'Requests', 'Forecast'],
    rows: [
      { label: 'Department budget', width: '76%', offset: '5%' },
      { label: 'Approval queue', width: '54%', offset: '27%' },
      { label: 'Monthly forecast', width: '62%', offset: '16%' }
    ]
  }
};

function LockedFeaturePreview({ featureKey, featureName, workspace, children }) {
  const config = previewConfig[featureKey] || {
    title: featureName || 'Premium workspace',
    columns: ['Overview', 'Details', 'Insights'],
    rows: [
      { label: 'Premium capability', width: '72%', offset: '8%' },
      { label: 'Advanced workflow', width: '52%', offset: '24%' },
      { label: 'Plan-only data', width: '40%', offset: '42%' }
    ]
  };

  return (
    <div className="relative min-h-[560px] overflow-hidden bg-[#f8fafc] px-4 py-8 dark:bg-slate-950 sm:px-6">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(20,184,166,0.12),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(251,191,36,0.12),transparent_28%)]" />

      <div
        className="relative mx-auto max-w-6xl select-none opacity-65 blur-[3px]"
        aria-hidden="true"
      >
        <div className="mb-5 flex items-center justify-between">
          <div>
            <div className="h-3 w-28 rounded-full bg-slate-300/80 dark:bg-white/15" />
            <div className="mt-3 h-8 w-64 rounded-2xl bg-white shadow-sm dark:bg-white/10" />
          </div>
          <div className="flex gap-2">
            <div className="h-10 w-10 rounded-2xl bg-white shadow-sm dark:bg-white/10" />
            <div className="h-10 w-28 rounded-2xl bg-slate-900/80 dark:bg-white/80" />
          </div>
        </div>

        <div className="overflow-hidden rounded-[28px] border border-white bg-white/90 shadow-2xl shadow-slate-950/10 dark:border-white/10 dark:bg-white/5">
          <div className="border-b border-slate-200/70 p-5 dark:border-white/10">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="text-sm font-semibold text-slate-500 dark:text-slate-400">{workspace === 'organization' ? 'Organization' : 'Personal'} preview</div>
                <div className="mt-1 text-2xl font-semibold text-slate-950 dark:text-white">{config.title}</div>
              </div>
              <div className="h-10 w-32 rounded-full bg-slate-100 dark:bg-white/10" />
            </div>
          </div>

          <div className="grid grid-cols-12 gap-0">
            <div className="col-span-3 border-r border-slate-200/70 p-5 dark:border-white/10">
              {[0, 1, 2, 3, 4].map((item) => (
                <div key={item} className="mb-4 rounded-2xl bg-slate-50/80 p-4 dark:bg-white/5">
                  <div className="h-3 w-2/3 rounded-full bg-slate-300/80 dark:bg-white/15" />
                  <div className="mt-3 h-2 w-full rounded-full bg-slate-200 dark:bg-white/10" />
                  <div className="mt-2 h-2 w-4/5 rounded-full bg-slate-200 dark:bg-white/10" />
                </div>
              ))}
            </div>

            <div className="col-span-9 p-5">
              <div className="mb-5 grid grid-cols-3 gap-3">
                {config.columns.map((column) => (
                  <div key={column} className="rounded-2xl bg-slate-100/80 p-4 dark:bg-white/5">
                    <div className="text-sm font-semibold text-slate-500 dark:text-slate-400">{column}</div>
                    <div className="mt-4 h-16 rounded-2xl bg-white dark:bg-white/10" />
                  </div>
                ))}
              </div>

              <div className="space-y-4">
                {config.rows.map((row) => (
                  <div key={row.label} className="relative h-16 rounded-2xl bg-slate-50/80 dark:bg-white/5">
                    <div className="absolute left-4 top-5 h-3 w-32 rounded-full bg-slate-300/80 dark:bg-white/15" />
                    <div
                      className="absolute top-4 h-8 rounded-full bg-slate-900/80 dark:bg-white/80"
                      style={{ left: row.offset, width: row.width }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute inset-0 bg-white/45 backdrop-blur-[7px] dark:bg-slate-950/55" />

      <div className="absolute inset-x-0 top-10 z-10 mx-auto flex max-w-2xl px-4 sm:top-16">
        {children}
      </div>
    </div>
  );
}

const PlanFeatureGate = ({
  featureKey,
  featureName,
  workspace = 'personal',
  children,
  fallback = null
}) => {
  const { loading, error, hasFeature } = usePlanEntitlements({ workspace });
  const { setHeader, clearHeader } = useHeader();

  useLayoutEffect(() => {
    if (featureName) {
      setHeader({
        pageTitle: featureName,
        pageSubtitle: workspace === 'organization' ? 'Organization workspace' : 'Personal workspace',
        pageContext: featureKey || null,
      });
    }
    return () => {
      if (featureName) clearHeader();
    };
  }, [clearHeader, featureKey, featureName, setHeader, workspace]);

  // Finance owns its own trial/subscription screen. It must render while the
  // independent app subscription is inactive so an admin can activate it;
  // Finance endpoints remain protected by orgFinanceAccess on the backend.
  if (featureKey === 'module_finance') return children;

  if (loading) {
    return (
      <div className="flex min-h-[320px] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-gray-200 border-t-indigo-600" />
      </div>
    );
  }

  if (error || !hasFeature(featureKey)) {
    if (fallback) return fallback;
    return (
      <LockedFeaturePreview featureKey={featureKey} featureName={featureName} workspace={workspace}>
        <UpgradePrompt
          feature={featureName || featureKey}
          message={`${featureName || 'This feature'} is not included in your current ${workspace === 'organization' ? 'organization' : 'workspace'} plan.`}
          itemId={featureKey}
          code="FEATURE_UNAVAILABLE"
          limit={0}
          current={0}
          workspaceType={workspace}
          upgradeUrl={workspaceUpgradeUrl[workspace] || '/pricing'}
        />
      </LockedFeaturePreview>
    );
  }

  return children;
};

export default PlanFeatureGate;
